/**
 * Editorial copy for existing-owner upgrade comparisons.
 * Numbers still come from getUpgradeComparisonFacts.
 */

export function getUpgradeEditorial(config, facts) {
  if (config.id === 'lvs34-vs-lvs44') {
    return buildLvs44Editorial(config, facts);
  }
  return buildLvs42hdEditorial(config, facts);
}

function buildLvs42hdEditorial(config, facts) {
  const considered = config.consideredProduct.shortLabel;
  return {
    sectionOrder: ['beam', 'sep', 'range', 'hardware'],
    notAutomatic: `Do not upgrade just because ${considered} is newer.`,
    keepHeading: 'Keep your LVS34 if',
    keepItems: [
      'It already does what you need.',
      `You regularly need more than ${facts.consideredMaxFt} ft of range.`,
      'Your current screen is not LiveScope 2 compatible and you do not want to replace it.',
    ],
    upgradeHeading: `Upgrade to ${considered} if`,
    upgradeItems: [
      `Most of your fishing is within ${facts.consideredMaxFt} ft.`,
      'Wider field of view and better close-range detail matter to you.',
      'Removing the GLS 10 and simplifying your setup matter to you.',
    ],
    priceJumpLabel: `See current ${considered} prices ↓`,
    alternatePath: null,
    beamHeading: 'See more water',
    beamCopy:
      'LVS42HD has a wider field of view in every mode, with the biggest difference in Down and Perspective.',
    beamConclusions: {
      forward: 'LVS42HD adds 30° of Forward field of view.',
      down: 'LVS42HD adds 45° of Down field of view.',
      perspective: 'LVS42HD adds 45° of Perspective field of view.',
    },
    beamModelId: 'lvs42hd',
    sepHeading: 'See closer targets more clearly',
    sepIntro:
      'How close can two fish or objects be before LiveScope can still show them as two separate targets? Smaller is better.',
    sepConsideredLabel: considered,
    sep30: {
      exampleIn: 5,
      ownedThresholdIn: 6.3,
      consideredThresholdIn: 3.8,
      takeaway:
        'At 5" apart, LVS42HD should show two separate targets. LVS34 would likely blend them together.',
      support:
        'LVS42HD needs 3.8" of spacing. Angler Built estimates LVS34 needs about 6.3".',
    },
    sep100: {
      exampleIn: 13,
      ownedThresholdIn: 14,
      consideredThresholdIn: 12.6,
      takeaway:
        'At 13" apart, LVS42HD should show two separate targets. LVS34 would likely blend them together.',
      support: 'LVS42HD needs 12.6" of spacing. LVS34 needs 14.0".',
    },
    rangeHeading: 'LVS34 still goes farther',
    rangeCopy: `LVS34 goes to ${facts.ownedMaxFt} ft. LVS42HD goes to ${facts.consideredMaxFt} ft.`,
    rangeA11y: `LVS34 goes to ${facts.ownedMaxFt} ft. LVS42HD goes to ${facts.consideredMaxFt} ft. LVS42HD is not the range upgrade.`,
    rangeOutcome: 'Want longer range? Keep the LVS34.',
    sepA11y:
      'How close can two fish or objects be before LiveScope can still show them as two separate targets? Smaller is better. Closer look at 30 ft. Targets are 5.0 in apart. LVS34 would likely blend them. LVS42HD should show two targets. LVS34 needs about 6.3 in, an Angler Built estimate. LVS42HD needs 3.8 in. Closer look at 100 ft. Targets are 13.0 in apart. LVS34 would likely blend them. LVS42HD should show two targets. LVS34 needs 14.0 in. LVS42HD needs 12.6 in.',
    rangeGain: null,
    hardwareCopy:
      'LVS34 requires the GLS 10 black box. LVS42HD connects directly to a compatible Garmin screen.',
    hardwareImageTestId: 'upgrade-hw-lvs42hd',
    hardwareImageClass: 'ab-upg-hw-photo-lvs42',
    hardwareImageAlt: 'Garmin LVS42HD transducer',
    showIceWeight: true,
    hardwareTakeaway: 'One less box, about 3 lb less hardware shown.',
    faqs: lvs42hdFaqs(config, facts, considered),
  };
}

function buildLvs44Editorial(config, facts) {
  const considered = config.consideredProduct.shortLabel;
  const plusFt = facts.consideredMaxFt - facts.ownedMaxFt;
  const percent = Math.round((plusFt / facts.ownedMaxFt) * 100);
  return {
    sectionOrder: ['range', 'beam', 'sep', 'hardware'],
    notAutomatic: `Do not upgrade just because ${considered} is newer.`,
    keepHeading: 'Keep your LVS34 if',
    keepItems: [
      'It already does what you need.',
      'You rarely need more than 200 ft of scanning range.',
      'The GLS 10 is not creating a meaningful problem in your setup.',
      'Your current screen is not LiveScope 2 compatible and you do not want to replace it.',
    ],
    upgradeHeading: `Upgrade to ${considered} if`,
    upgradeItems: [
      'You regularly want to scan beyond 200 ft.',
      'Wider field of view would help you cover water faster.',
      'Removing the GLS 10 would meaningfully simplify your setup.',
      'Your current screen is compatible, or you already planned to upgrade it.',
    ],
    priceJumpLabel: `See current ${considered} prices ↓`,
    alternatePath: {
      before: 'Want the biggest close-range detail improvement instead? ',
      label: 'Compare LVS42HD',
      href: '/comparisons/lvs34-vs-lvs42hd',
      after: '.',
    },
    beamHeading: 'See more water at once',
    beamCopy:
      'LVS44 has a wider field of view in every mode, with the largest difference in Down and Perspective.',
    beamConclusions: {
      forward: 'LVS44 adds 30° of Forward field of view.',
      down: 'LVS44 adds 45° of Down field of view.',
      perspective: 'LVS44 adds 45° of Perspective field of view.',
    },
    beamModelId: 'lvs44',
    sepHeading: 'Targets separate a little sooner',
    sepIntro:
      'How close can two fish or objects be before LiveScope can still show them as two separate targets? Smaller is better.',
    sepConsideredLabel: considered,
    sep30: {
      exampleIn: 6,
      ownedThresholdIn: 6.3,
      consideredThresholdIn: 5.7,
      takeaway:
        'At 6" apart, LVS44 should show two separate targets. LVS34 would likely blend them together.',
      support:
        'LVS44 needs 5.7" of spacing. Angler Built estimates LVS34 needs about 6.3".',
    },
    sep100: {
      exampleIn: 13,
      ownedThresholdIn: 14,
      consideredThresholdIn: 12.6,
      takeaway:
        'At 13" apart, LVS44 should show two separate targets. LVS34 would likely blend them together.',
      support: 'LVS44 needs 12.6" of spacing. LVS34 needs 14.0".',
    },
    rangeHeading: 'Scan 50 ft farther',
    rangeCopy: `LVS34 is rated to ${facts.ownedMaxFt} ft. LVS44 extends that to ${facts.consideredMaxFt} ft.`,
    rangeA11y: `LVS34 goes to ${facts.ownedMaxFt} ft. LVS44 goes to ${facts.consideredMaxFt} ft. That is ${plusFt} ft additional range and ${percent}% more rated range.`,
    rangeOutcome:
      'Regularly scanning past 200 ft? This is the clearest reason to upgrade to LVS44.',
    sepA11y:
      'How close can two fish or objects be before LiveScope can still show them as two separate targets? Smaller is better. Closer look at 30 ft. Targets are 6.0 in apart. LVS34 would likely blend them. LVS44 should show two targets. LVS34 needs about 6.3 in, an Angler Built estimate. LVS44 needs 5.7 in. Closer look at 100 ft. Targets are 13.0 in apart. LVS34 would likely blend them. LVS44 should show two targets. LVS34 needs 14.0 in. LVS44 needs 12.6 in.',
    rangeGain: {
      plusLabel: `+${plusFt} ft`,
      percentLabel: `${percent}% more rated range`,
    },
    hardwareCopy:
      'LVS34 requires the GLS 10 black box. LVS44 connects directly to a compatible Garmin screen.',
    hardwareImageTestId: 'upgrade-hw-lvs44',
    hardwareImageClass: 'ab-upg-hw-photo-lvs42',
    hardwareImageAlt: 'Garmin LVS44 transducer',
    showIceWeight: false,
    hardwareTakeaway: 'One less box, about 2.7 lb less hardware shown.',
    faqs: lvs44Faqs(config, facts),
  };
}

function lvs42hdFaqs(config, facts, considered) {
  return [
    {
      testId: 'upgrade-faq-screen',
      summary: 'Will LVS42HD work with my current Garmin screen?',
      paragraphs: [
        'LiveScope 2 does not work with every Garmin screen that can run LVS34. Check your current Garmin screen before you buy.',
      ],
      link: { to: config.compatibilityPath, label: 'Check Your Screen' },
    },
    {
      testId: 'upgrade-faq-gls',
      summary: 'Does LVS42HD still need the GLS 10 black box?',
      paragraphs: [
        'No. LVS34 uses the GLS 10 sonar module. LVS42HD connects to a compatible Garmin screen and does not use GLS 10.',
      ],
    },
    {
      testId: 'upgrade-faq-beam',
      summary: 'How much wider is the beam?',
      paragraphs: [
        `Garmin publishes ${facts.ownedForwardSweep}° field of view for LVS34 and ${facts.consideredForwardSweep}° Forward / ${facts.consideredDownSweep}° Down or Perspective for LVS42HD. That is ${facts.consideredForwardSweep - facts.ownedForwardSweep}° wider Forward coverage and ${facts.consideredDownSweep - facts.ownedDownSweep}° wider Down and Perspective coverage.`,
      ],
    },
    {
      testId: 'upgrade-more-sep',
      summary: 'How were the target-separation numbers derived?',
      paragraphs: [
        'Verified values: LVS42HD 3.8 in at 30 ft and 12.6 in at 100 ft, LVS44 5.7 in at 30 ft and 12.6 in at 100 ft, and LVS34 14.0 in at 100 ft. Lower is better.',
        'Garmin does not provide an LVS34 measurement at 30 ft. Angler Built estimates about 6.3 in from the LVS34 vs LVS44 relationship at 100 ft: 14.0 / 12.6 is about 1.111, and 5.7 × 1.111 is about 6.33, rounded to about 6.3 in. This estimate is not a Garmin measurement. The 30 ft comparison visual uses this estimate. Angler Built does not use it to infer a complete LVS34 target-separation curve.',
      ],
      garminClaim: true,
    },
    {
      testId: 'upgrade-more-range',
      summary: 'Why does LVS42HD have less range?',
      paragraphs: [
        `LVS34 goes to ${facts.ownedMaxFt} ft. LVS42HD goes to ${facts.consideredMaxFt} ft. LVS42HD is the closer-range path, not a range upgrade. Want longer range? Keep the LVS34.`,
      ],
    },
    {
      testId: 'upgrade-more-gls',
      summary: 'How much weight do I actually save?',
      paragraphs: [
        `The LVS34 transducer is ${facts.ownedWeightLabel} transducer-only and the GLS 10 is ${facts.glsWeightLabel} module-only, or ${facts.publishedComponentLabel} together. LVS42HD is ${facts.consideredWeightLabel} with its manufacturer-defined inclusion basis. The dedicated ice version, LVS42HD-IF, is ${facts.iceWeightLabel} with its manufacturer-defined inclusion basis. That is about 3 lb difference in the hardware shown on this page. Installed weight still varies with cables, mounts, and the rest of the setup, because Garmin does not weigh these parts the same way.`,
      ],
    },
    {
      testId: 'upgrade-faq-use',
      summary: 'Is LVS42HD better for ice fishing or open-water fishing?',
      paragraphs: [
        `The same range, coverage, and detail trade-offs apply in both settings. Keep LVS34 if you regularly scan beyond ${facts.consideredMaxFt} ft. LVS42HD is the closer-range LiveScope 2 path. A separate LVS42HD-IF kit exists for portable ice. This page compares standard LVS42HD to LVS34.`,
      ],
    },
    {
      testId: 'upgrade-faq-lvs44',
      summary: 'Should I consider LVS44 instead?',
      inlineLink: {
        before:
          'This page is only LVS34 versus LVS42HD. If you are choosing among LiveScope 2 models, see the ',
        to: '/comparisons/lvs42hd-vs-lvs44',
        label: 'LiveScope 2 comparison',
        after: '. LVS44 is the farther-range LiveScope 2 path.',
      },
    },
    {
      testId: 'upgrade-spec-table',
      summary: 'What are the full Garmin-published specifications?',
      specTable: true,
    },
    {
      testId: 'upgrade-faq-sources',
      summary: 'Where do these numbers come from?',
      sources: true,
    },
  ];
}

function lvs44Faqs(config, facts) {
  return [
    {
      testId: 'upgrade-faq-screen',
      summary: 'Will LVS44 work with my current Garmin screen?',
      paragraphs: [
        'LiveScope 2 does not work with every Garmin screen. Check your current Garmin screen before you buy.',
      ],
      link: { to: config.compatibilityPath, label: 'Check Your Screen' },
    },
    {
      testId: 'upgrade-faq-gls',
      summary: 'Does LVS44 still need the GLS 10 black box?',
      paragraphs: [
        'No. LVS34 uses the GLS 10 sonar module. LVS44 connects to a compatible Garmin screen and does not use GLS 10.',
      ],
    },
    {
      testId: 'upgrade-more-range',
      summary: 'How much farther can LVS44 see?',
      paragraphs: [
        `LVS34 is rated to ${facts.ownedMaxFt} ft. LVS44 is rated to ${facts.consideredMaxFt} ft. That is 50 ft farther, or 25% more rated range. This is the clearest objective LVS44 upgrade advantage.`,
      ],
    },
    {
      testId: 'upgrade-faq-beam',
      summary: 'How much wider is the beam?',
      paragraphs: [
        `Garmin publishes ${facts.ownedForwardSweep}° field of view for LVS34 and ${facts.consideredForwardSweep}° Forward / ${facts.consideredDownSweep}° Down or Perspective for LVS44. That is 30° wider Forward coverage and 45° wider Down and Perspective coverage.`,
      ],
    },
    {
      testId: 'upgrade-more-sep',
      summary: 'How much better is target separation?',
      paragraphs: [
        'At 100 ft, Garmin publishes 14.0 in for LVS34 and 12.6 in for LVS44. At 30 ft, Garmin publishes 5.7 in for LVS44. Garmin does not provide an LVS34 measurement at 30 ft. Angler Built estimates about 6.3 in from the LVS34 vs LVS44 relationship at 100 ft: 14.0 / 12.6 is about 1.111, and 5.7 × 1.111 is about 6.33, rounded to about 6.3 in. This estimate is not a Garmin measurement. The improvement is real, but modest compared with the LVS42HD close-range story.',
      ],
    },
    {
      testId: 'upgrade-faq-resolution',
      summary: 'What does Garmin mean by 20% more resolution?',
      paragraphs: [
        'Garmin markets LVS44 as offering 20% more resolution. Resolution and minimum target separation are different measurements. This page uses the published and estimated target-separation values instead of treating 20% more resolution as 20% better target separation, 20% clearer fish, or 20% more detail at every distance.',
      ],
    },
    {
      testId: 'upgrade-faq-detail',
      summary: 'Why is LVS44 not the best choice for maximum close-range detail?',
      paragraphs: [
        'LVS44 improves target separation, but the gain is modest next to LVS42HD. If your main goal is the biggest close-range detail improvement, LVS42HD is the more relevant upgrade path.',
      ],
    },
    {
      testId: 'upgrade-faq-lvs42hd',
      summary: 'Should I consider LVS42HD instead?',
      paragraphs: [
        'Choose LVS42HD if close-range target separation is the reason you would replace an LVS34 that already works. Choose LVS44 if you want 50 ft more rated range, wider coverage, and no GLS 10.',
      ],
      link: { to: '/comparisons/lvs34-vs-lvs42hd', label: 'Compare LVS42HD' },
    },
    {
      testId: 'upgrade-more-gls',
      summary: 'How much hardware weight do I actually save?',
      paragraphs: [
        `The LVS34 transducer is ${facts.ownedWeightLabel} transducer-only and the GLS 10 is ${facts.glsWeightLabel} module-only, or ${facts.publishedComponentLabel} together. LVS44 is ${facts.consideredWeightLabel} with its manufacturer-defined inclusion basis. That is about 2.7 lb difference in the hardware shown on this page. Installed weight still varies with cables, mounts, and the rest of the setup, because Garmin does not weigh these parts the same way.`,
      ],
    },
    {
      testId: 'upgrade-spec-table',
      summary: 'What are the full Garmin specifications?',
      specTable: true,
    },
    {
      testId: 'upgrade-faq-sources',
      summary: 'Where do these numbers come from?',
      sources: true,
    },
  ];
}
